import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Box, Typography, MenuItem, TextField } from '@mui/material'

import { ReactComponent as CalendarBlank } from '../../../../assets/icons/calendar-blank.svg'

import useIsMobile from '../../../../hooks/useIsMobile'
import useTranslations from '../../../../i18n/useTranslations'
import DatePicker from '../../../../ui/components/datePicker'

import styles from './styles'
import { checkIsNotIncluded } from '../../../../libs/datePicker'
import Select from '../../../../ui/components/select'
import { SCHEDULE_OPTIONS } from './index'

export const formatPickupId = pickupId => {
  const [minDate, maxDate] = pickupId.split('/')

  return {
    minDate: new Date(minDate),
    maxDate: new Date(maxDate),
  }
}

const propTypes = {
  firstPickupDate: PropTypes.string,
  selectedDay: PropTypes.object,
  selectedSchedule: PropTypes.string,
  selectedTimezone: PropTypes.string,
  enabledCalendarDays: PropTypes.string,
  changeDay: PropTypes.func,
  changeSchedule: PropTypes.func,
  changeTimezone: PropTypes.func,
  changePickupDate: PropTypes.func,
  scheduleOptions: PropTypes.arrayOf(
    // TODO especificar las propiedades del horario
    PropTypes.object,
  ),
  timezoneOptions: PropTypes.arrayOf(
    // TODO especificar las propiedades de la franja de recogida
    PropTypes.object,
  ),
  hasFixedPickup: PropTypes.bool,
}

const ChoosePickup = ({
  changeDay,
  selectedDay,
  enabledCalendarDays,
  changeSchedule,
  selectedSchedule,
  scheduleOptions,
  timezoneOptions,
  selectedTimezone,
  changeTimezone,
  changePickupDate,
  firstPickupDate,
  hasFixedPickup,
}) => {
  const formatMessage = useTranslations()
  const isMobile = useIsMobile()

  const [chosenDate, setChosenDate] = useState()

  useEffect(() => {
    changePickupDate(null)

    return () => {
      changePickupDate(firstPickupDate)
      changeDay('')
      changeSchedule('none')
      changeTimezone('none')
    }
  }, [])

  return (
    <>
      <Box sx={styles.fields}>
        <DatePicker
          error={false}
          name="pickupDay"
          label={formatMessage('noun:pick-up-date')}
          placeholder={formatMessage('placeholder:date')}
          autoOk={true}
          autocomplete="off"
          defaultValue={selectedDay}
          value={chosenDate}
          renderInput={params => (
            <TextField onKeyDown={evt => evt.preventDefault()} {...params} />
          )}
          onChange={date => {
            // TODO: Temporary fix | take in account two methods to set date
            // When date is introduced by the input date is an event
            // however when date is introduced by the calendar date is a Date
            if (date?.target && date?.target?.value) {
              // TODO: Temporary fix | issuw when reading and setting date
              // input is showing date in format dd/MM/yyyy
              // but is setted in format MM/dd/yyyy
              // Also changeDay use a Date, taht need to be in format MM/dd/yyyy
              const date_ddMMyyyy = date.target.value.split('/')
              const date_MMddyyyy = `${date_ddMMyyyy[1] ?? '01'}/${
                date_ddMMyyyy[0] ?? '01'
              }/${date_ddMMyyyy[2] ?? '2024'}`

              const newDate = new Date(date_MMddyyyy)
              if (newDate.toString() !== 'Invalid Date') {
                setChosenDate(date_MMddyyyy)
                changeDay(newDate)
                changePickupDate(null)
                changeSchedule('none')
                changeTimezone('none')
              }
            } else {
              setChosenDate(date)
              changeDay(date)
              changePickupDate(null)
              changeSchedule('none')
              changeTimezone('none')
            }
          }}
          minDate={new Date()}
          className={isMobile ? 'input-full' : 'input-small'}
          shouldDisableDate={date =>
            checkIsNotIncluded(date, enabledCalendarDays)
          }
        />
        <Select
          disabled={selectedDay === ''}
          label={formatMessage('noun:timetable')}
          labelId="pick-up-timetable"
          name="pickupSchedule"
          sx={styles.selectField}
          className={isMobile ? 'input-full' : null}
          value={selectedSchedule}
          defaultValue="none"
          onChange={event => {
            const schedule = event.target.value
            changeSchedule(schedule)

            if (schedule !== SCHEDULE_OPTIONS.chooseTimezone) {
              changePickupDate(schedule)
            } else {
              changePickupDate(null)
              changeTimezone('none')
            }
          }}
        >
          <MenuItem disabled value="none">
            <em>
              {formatMessage(
                'shipping-send-form:origin:pickup:choose-schedule',
              )}
            </em>
          </MenuItem>
          {scheduleOptions.map(schedule => {
            return (
              <MenuItem key={schedule.i18nKey} value={schedule.value}>
                {formatMessage(schedule.i18nKey, schedule.i18nOptions)}
              </MenuItem>
            )
          })}
        </Select>
        {selectedSchedule === SCHEDULE_OPTIONS.chooseTimezone ? (
          <>
            <Select
              label={formatMessage('noun:time-slot')}
              labelId="pick-up-time-slot"
              name="pickupTimezone"
              defaultValue="none"
              value={selectedTimezone}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={event => {
                const timezone = event.target.value
                changeTimezone(timezone)
                changePickupDate(timezone)
              }}
              className={isMobile ? 'input-full' : 'input-small'}
            >
              <MenuItem disabled value="none">
                <em>Elije la franja horaria</em>
              </MenuItem>
              {timezoneOptions.map(timezone => {
                return (
                  <MenuItem key={timezone} value={timezone}>
                    {formatMessage(
                      'shipping-send-form:time-slot-option',
                      formatPickupId(timezone),
                    )}
                  </MenuItem>
                )
              })}
            </Select>
          </>
        ) : null}
      </Box>
      <Alert
        severity={
          (selectedSchedule !== 'none' &&
            selectedSchedule !== SCHEDULE_OPTIONS.chooseTimezone) ||
          selectedTimezone !== 'none'
            ? 'success'
            : 'info'
        }
        icon={
          (selectedSchedule !== 'none' &&
            selectedSchedule !== SCHEDULE_OPTIONS.chooseTimezone) ||
          selectedTimezone !== 'none' ? (
            <CalendarBlank />
          ) : undefined
        }
        sx={{ ...styles.alert, ...styles.alertEarlyDate }}
      >
        <Typography variant="h3medium">
          {selectedDay === ''
            ? formatMessage('message:choose-day')
            : selectedSchedule === 'none'
            ? formatMessage('message:choose-schedule')
            : selectedSchedule === SCHEDULE_OPTIONS.chooseTimezone
            ? selectedTimezone === 'none'
              ? formatMessage('message:choose-timeslot')
              : formatMessage(
                  'shipping-send-form:pickup-date',
                  formatPickupId(selectedTimezone),
                )
            : formatMessage(
                'shipping-send-form:pickup-date',
                formatPickupId(selectedSchedule),
              )}
        </Typography>
      </Alert>
      {hasFixedPickup && (
        <Alert severity="warning" sx={styles.alert}>
          <Typography variant="body2">
            {formatMessage('message:choose-pickup-with-fixed-pickup')}
          </Typography>
        </Alert>
      )}
    </>
  )
}

ChoosePickup.propTypes = propTypes

export default ChoosePickup
